import { useCallback } from 'react';
import { useAppDispatch } from '../store';
import { sendPurchaseEvent } from '../../analytics';
import { setGoogleAdsPrimaryPurchaseContext } from '../store/googleAds';
import {
  buildPrimaryPurchaseCompletion,
  type PrimaryPurchaseCompletionDefaults,
  type PrimaryPurchaseCompletionInput,
  type PrimaryPurchaseEventDefaults,
} from '../utils/primaryPurchaseCompletion';

export interface CompletePrimaryPurchaseInput extends PrimaryPurchaseCompletionInput {
  completeProviderUi?: () => void;
}

export type CompletePrimaryPurchase = (input: CompletePrimaryPurchaseInput) => void;

type UseCompletePrimaryPurchaseDefaults = Omit<
  PrimaryPurchaseCompletionDefaults,
  keyof PrimaryPurchaseEventDefaults
> & {
  resolvePurchaseEventDefaults: () => PrimaryPurchaseEventDefaults;
};

export const useCompletePrimaryPurchase = (
  defaults: UseCompletePrimaryPurchaseDefaults,
): { completePrimaryPurchase: CompletePrimaryPurchase } => {
  const dispatch = useAppDispatch();
  const {
    email,
    geo,
    gclid,
    fbclid,
    ttclid,
    flowType,
    landing,
    userLocation,
    useTimerDefaultLTV,
    resolvePurchaseEventDefaults,
  } = defaults;

  const completePrimaryPurchase = useCallback<CompletePrimaryPurchase>(
    ({ completeProviderUi, ...input }) => {
      const purchaseEventDefaults = resolvePurchaseEventDefaults();
      const completion = buildPrimaryPurchaseCompletion({
        defaults: {
          email,
          geo,
          gclid,
          fbclid,
          ttclid,
          flowType,
          landing,
          userLocation,
          useTimerDefaultLTV,
          ...purchaseEventDefaults,
        },
        input,
      });

      dispatch(setGoogleAdsPrimaryPurchaseContext(completion.googleAdsContext));
      completeProviderUi?.();
      sendPurchaseEvent(completion.purchaseEventData);
    },
    [
      dispatch,
      email,
      geo,
      gclid,
      fbclid,
      ttclid,
      flowType,
      landing,
      userLocation,
      useTimerDefaultLTV,
      resolvePurchaseEventDefaults,
    ],
  );

  return { completePrimaryPurchase };
};
